import Phaser from 'phaser';
import { pointKey } from '../data/levels';
import type { GridPoint, HazardKind } from '../types';

const LABELS: Record<HazardKind, string> = {
  'malware-bug': 'DIGITAL\nTHREAT', 'packet-loss': 'OUTAGE', 'spam-bot': 'OLD\nEQUIPMENT',
  'phishing-trap': 'DEAD\nZONE', 'broken-zone': 'BROKEN\nCABLE',
};

export class DigitalHazard extends Phaser.GameObjects.Container {
  cell: GridPoint;
  readonly kind: HazardKind;
  private step = 0;

  constructor(scene: Phaser.Scene, kind: HazardKind, cell: GridPoint, x: number, y: number, reducedMotion: boolean, textScale = 1) {
    super(scene, x, y);
    this.kind = kind;
    this.cell = { ...cell };
    scene.add.existing(this);
    this.setDepth(15);
    this.draw(scene, textScale, reducedMotion);
    if (!reducedMotion) scene.tweens.add({ targets: this, angle: { from: -4, to: 4 }, duration: kind === 'phishing-trap' ? 320 : 560, yoyo: true, repeat: -1 });
  }

  move(paths: Set<string>, toWorld: (point: GridPoint) => { x: number; y: number }, reducedMotion: boolean, player?: GridPoint, exclusions: Set<string> = new Set()): void {
    if (this.kind === 'broken-zone' || this.kind === 'phishing-trap') return;
    const choices = [
      { x: this.cell.x + 1, y: this.cell.y }, { x: this.cell.x - 1, y: this.cell.y },
      { x: this.cell.x, y: this.cell.y + 1 }, { x: this.cell.x, y: this.cell.y - 1 },
    ].filter((point) => paths.has(pointKey(point)) && !exclusions.has(pointKey(point)));
    let next: GridPoint | undefined;
    if (this.kind === 'malware-bug' && player) {
      next = [...choices].sort((a, b) => Math.abs(a.x - player.x) + Math.abs(a.y - player.y) - Math.abs(b.x - player.x) - Math.abs(b.y - player.y))[0];
    } else if (this.kind === 'spam-bot') {
      next = choices[this.step % choices.length];
      this.step += 1;
    } else next = Phaser.Utils.Array.GetRandom(choices);
    if (!next) return;
    this.cell = next;
    const world = toWorld(next);
    if (reducedMotion) this.setPosition(world.x, world.y);
    else this.scene.tweens.add({ targets: this, x: world.x, y: world.y, duration: this.kind === 'packet-loss' ? 430 : 280, ease: 'Sine.InOut' });
  }

  private draw(scene: Phaser.Scene, textScale: number, reducedMotion: boolean): void {
    const warning = scene.add.graphics();
    const warningColor = this.kind === 'spam-bot' ? 0xffc145 : this.kind === 'broken-zone' ? 0xff5f6d : this.kind === 'phishing-trap' ? 0x9d7cff : 0x8fdfff;
    warning.lineStyle(4, warningColor, 0.75).strokeCircle(0, 0, 32);
    warning.fillStyle(warningColor, 0.12).fillCircle(0, 0, 31);
    this.add(warning);
    if (reducedMotion) warning.setAlpha(0.85);
    else scene.tweens.add({ targets: warning, alpha: { from: 0.38, to: 1 }, scale: { from: 0.92, to: 1.08 }, duration: 520, yoyo: true, repeat: -1 });
    const graphic = scene.add.graphics();
    if (this.kind === 'malware-bug') {
      graphic.fillStyle(0xff5f6d, 1).fillRoundedRect(-18, -15, 36, 30, 10);
      graphic.lineStyle(4, 0xffb1b8, 1);
      [-11, 0, 11].forEach((y) => { graphic.lineBetween(-22, y, -14, y * 0.55); graphic.lineBetween(22, y, 14, y * 0.55); });
      graphic.fillStyle(0xffffff, 1).fillCircle(-6, -4, 3).fillCircle(6, -4, 3);
    } else if (this.kind === 'packet-loss') {
      graphic.fillStyle(0x9d7cff, 1).fillCircle(-11, 3, 12).fillCircle(1, -6, 15).fillCircle(14, 4, 11);
      graphic.fillStyle(0x06152f, 1).fillRect(-12, 1, 25, 5);
      graphic.lineStyle(3, 0xffffff, 0.8).lineBetween(-16, -14, 17, 16);
    } else if (this.kind === 'spam-bot') {
      graphic.fillStyle(0xffc145, 1).fillRoundedRect(-19, -17, 38, 34, 7);
      graphic.fillStyle(0x06152f, 1).fillRect(-11, -7, 22, 4).fillRect(-11, 2, 16, 4);
      graphic.lineStyle(3, 0xffc145, 1).lineBetween(0, -17, 0, -27);
      graphic.fillStyle(0x1ed8ff, 1).fillCircle(0, -28, 4);
      graphic.lineStyle(3, 0xffffff, 0.8).lineBetween(-29, -5, -23, -11).lineBetween(29, -5, 23, -11);
    } else if (this.kind === 'phishing-trap') {
      graphic.lineStyle(5, 0xff5f6d, 1).lineBetween(-5, -24, -5, 12);
      graphic.lineStyle(5, 0xffffff, 0.9).arc(-5, 12, 17, 0, Math.PI, false);
      graphic.fillStyle(0xff5f6d, 0.32).fillTriangle(-22, 16, 13, 16, -5, 32);
    } else {
      graphic.fillStyle(0xff8a34, 0.18).fillCircle(0, 0, 28);
      graphic.lineStyle(7, 0xff8a34, 1).lineBetween(-16, -16, 16, 16).lineBetween(16, -16, -16, 16);
      graphic.lineStyle(5, 0xff5f6d, 1).lineBetween(-31, 21, 31, 21);
    }
    this.add(graphic);
    const sideLabel = this.kind === 'phishing-trap';
    this.add(scene.add.text(sideLabel ? 43 : 0, sideLabel ? 0 : 36, LABELS[this.kind], {
      fontFamily: 'Arial Black, Arial', fontSize: `${Math.round(11 * textScale)}px`, color: '#ffffff', backgroundColor: '#020916f5', padding: { x: 6, y: 4 }, align: 'center', lineSpacing: 1,
    }).setOrigin(sideLabel ? 0 : 0.5, sideLabel ? 0.5 : 0));
  }
}
